/**
 * Utility functions to clean circular references from nodes before storage
 */

import { User, Workspace, Project, Task, Message, Document } from '@/types'

/**
 * Clean a user object by removing all relationship arrays
 */
export function cleanUser(user: User): User {
  return {
    ...user,
    workspaces: [],
    projects: [],
    tasks: [],
    messages: [],
    documents: [],
  }
}

/**
 * Clean multiple user objects
 */
export function cleanUsers(users: User[]): User[] {
  return users.map(cleanUser)
}

/**
 * Clean a workspace object by removing all relationship arrays
 * Preserves basic user/creator info but removes their relationships
 */
export function cleanWorkspace(workspace: Workspace): Workspace {
  return {
    ...workspace,
    creator: cleanUser(workspace.creator),
    users: cleanUsers(workspace.users),
    projects: [],
    tasks: [],
    messages: [],
    documents: [],
  }
}

/**
 * Clean a project object by removing all relationship arrays
 * Preserves workspace, creator, and users but cleans their relationships
 */
export function cleanProject(project: Project): Project {
  return {
    ...project,
    workspace: cleanWorkspace(project.workspace),
    creator: cleanUser(project.creator),
    users: cleanUsers(project.users),
    tasks: [],
    messages: [],
    documents: [],
  }
}

/**
 * Clean multiple project objects
 */
export function cleanProjects(projects: Project[]): Project[] {
  return projects.map(cleanProject)
}

/**
 * Clean a task object by removing all relationship arrays
 * Preserves workspace, creator, projects, assignee, and users but cleans their relationships
 * NOTE: Does NOT clean parentTask, dependencies, or subtasks to avoid infinite recursion
 * Those should be cleaned separately in init.service
 */
export function cleanTask(task: Task): Task {
  return {
    ...task,
    creator: cleanUser(task.creator),
    assignee: task.assignee ? cleanUser(task.assignee) : undefined,
    workspace: cleanWorkspace(task.workspace),
    projects: cleanProjects(task.projects),
    users: cleanUsers(task.users),
    // Keep these empty to avoid deep recursion during cleaning
    parentTask: undefined,
    subtasks: [],
    dependencies: [],
    messages: [],
    documents: [],
  }
}

/**
 * Clean multiple task objects
 */
export function cleanTasks(tasks: Task[]): Task[] {
  return tasks.map(cleanTask)
}

/**
 * Clean a message object by removing all relationship arrays except threaded messages
 * Preserves workspace, creator, users, but cleans their relationships
 * Recursively cleans nested message threads while preserving the thread structure
 */
export function cleanMessage(message: Message, depth: number = 0): Message {
  // Prevent excessive nesting (safety limit)
  const maxDepth = 10

  return {
    ...message,
    creator: cleanUser(message.creator),
    workspace: message.workspace ? cleanWorkspace(message.workspace) : message.workspace,
    users: cleanUsers(message.users),
    // Keep these empty to avoid deep recursion
    tasks: [],
    projects: [],
    documents: [],
    // Recursively clean nested messages to preserve thread structure
    messages:
      depth < maxDepth && message.messages
        ? message.messages.map((msg) => cleanMessage(msg, depth + 1))
        : [],
  }
}

/**
 * Clean multiple message objects
 */
export function cleanMessages(messages: Message[]): Message[] {
  return messages.map(cleanMessage)
}

/**
 * Clean a document object by removing all relationship arrays
 * Preserves workspace, creator, users, but cleans their relationships
 */
export function cleanDocument(document: Document): Document {
  return {
    ...document,
    creator: cleanUser(document.creator),
    workspace: cleanWorkspace(document.workspace),
    users: cleanUsers(document.users),
    tasks: [],
    projects: [],
    messages: [],
  }
}

/**
 * Clean multiple document objects
 */
export function cleanDocuments(documents: Document[]): Document[] {
  return documents.map(cleanDocument)
}
